
import { Component, Prop, Mixins } from 'vue-property-decorator';
import { AuthMixin, BAIconsMixin, StatusMixin, FormMixin, StripeMixin, PurchaseMixin, AthleteRoutingMixin, AthleteProfileMixin, StylesMixin } from '@/mixins';
import { BAEventModel, EventTicketModel, BAPurchaseModel } from '@/models'
import { PurchaseStep } from '@/../types/enums';
import { userStore } from '@/store';
import OnboardingDialog from '@/views/onboarding/OnboardingDialog.vue';
import { AgreementFile } from '@/models/file/FileModel';
import { baPurchaseApi } from '@/api/BAPurchaseApi';

type DateDataLabel = {
    label: string,
    date: Date;
}
@Component({
    components: {
        OnboardingDialog,
    }
})
export default class BestAthletesEventPurchaseDialog extends Mixins(BAIconsMixin, StatusMixin, StylesMixin, AuthMixin, FormMixin, StripeMixin, PurchaseMixin, AthleteRoutingMixin, AthleteProfileMixin) {
    @Prop() event: BAEventModel;
    @Prop() ticket: EventTicketModel;
    @Prop() color: string = "primary";
    @Prop({type: Boolean}) large;
    @Prop({type: Boolean}) xl;
    @Prop() purchaseStep: PurchaseStep;

    // types
    PurchaseStep = PurchaseStep;

    currentAgreement: number = -1;
    get HasCurrentAgreement(): boolean {
        return this.currentAgreement >= 0;
    }
    get CurrentAgreement(): AgreementFile {
        if( this.currentAgreement < 0 || this.currentAgreement >= this.event.agreements.length ) return undefined;
        return this.event.agreements[this.currentAgreement];
    }
    get DataLabels(): Array<string> {
        if( this.IsEmpty(this.event?.dataLabels) ) return [];
        return this.event.dataLabels;
    }
    NextPurchaseStep(): PurchaseStep {
        if( this.purchaseStep === PurchaseStep.Prepare ) {
            this.purchaseStep = PurchaseStep.Information;
        }
        else if( this.purchaseStep === PurchaseStep.Information ) {
            this.event.dataLabels.filter(l => this.NormStringIncludes(l, 'date')).forEach(dataLabel => {
                this.purchaseRequest.data[dataLabel] = this.formatDatePrettyShort(this.dateDataLabels.find(dateDataLabel => dateDataLabel.label === dataLabel).date, true);
            })

            this.isMissingRequiredData = this.DetermineMissingRequiredData();
            if( this.isMissingRequiredData ) {
                if( this.IsNotEmpty(this.requiredFieldMisformed) ) this.setError(`Incorrect format for: ${this.requiredFieldMisformed}`);
                else this.setError(`Missing required data: ${this.requiredFieldMissing}`);
                return;
            }

            if( this.IsEmptyArray(this.event.agreements) ) {
                this.purchaseStep = PurchaseStep.Review;
            } else {
                this.currentAgreement = 0;
                this.isAgreementIncomplete = !this.CurrentAgreement.signed;
                this.purchaseStep = PurchaseStep.Agreements;
            }
        }
        else if( this.purchaseStep === PurchaseStep.Agreements ) {
            this.purchaseStep = PurchaseStep.Information;
            if( this.currentAgreement >= (this.event.agreements.length - 1) ) 
                this.purchaseStep = PurchaseStep.Review;
            else {
                const nextAgreement = this.currentAgreement + 1;
                this.currentAgreement = -1;
                this.onScrollToTop();
                this.currentAgreement = nextAgreement;
                this.isAgreementIncomplete = !this.CurrentAgreement.signed;
                this.purchaseStep = PurchaseStep.Agreements;
            }
        }
        else if( this.purchaseStep === PurchaseStep.Review ) {
            this.ProceedToPayment();
        }
        else if( this.purchaseStep === PurchaseStep.Purchase ) {
        }
        
        return this.purchaseStep;
    }
    PrevPurchaseStep(): PurchaseStep {
        switch( this.purchaseStep ) {
            case PurchaseStep.Prepare:
                this.onHidePurchaseDialog();
                return( this.purchaseStep = undefined );
            case PurchaseStep.Information:
                return( this.purchaseStep = PurchaseStep.Prepare );
            case PurchaseStep.Agreements:
                return( this.purchaseStep = PurchaseStep.Information );
            case PurchaseStep.Review:
                if( this.IsEmptyArray(this.event.agreements) ) return( this.purchaseStep = PurchaseStep.Information );
                this.currentAgreement = 0;
                return( this.purchaseStep = PurchaseStep.Agreements );
            case PurchaseStep.Purchase:
                return( this.purchaseStep = PurchaseStep.Review );
        }
    }
    isAgreementIncomplete: boolean = true;
    onAgreementAccepted() {
        this.isAgreementIncomplete = !this.CurrentAgreement.signed;
    }
    onScrollToBottom() {
        const agreementCard = this.$refs.agreementCard as HTMLElement;
        agreementCard.scroll({
            top: agreementCard.scrollHeight,
            behavior: 'smooth'
        });
    }

    // The method below is now correctly named to reflect its function
    onScrollToTop() {
        const agreementCard = this.$refs.agreementCard as HTMLElement;
        agreementCard.scroll({
            top: 0,
            behavior: 'smooth'
        });
    }

    gotoEventWebsite() {
        if( this.IsEmpty(this.event?.url) ) return;

        window.open(this.event.url, "_blank");
    }

    dateDataLabels: Array<DateDataLabel> = [];
    async onOrderRequest() {
        if( this.IsEmpty(this.purchaseStep) ) this.purchaseStep = PurchaseStep.Prepare;

        this.athleteProfile = userStore.AthleteProfile;
        if( this.IsNotEmpty(this.athleteProfile) ) {
            await this.SyncAthleteData();
            this.dateDataLabels = this.DataLabels.map(label => { return {label, date: new Date(this.athleteProfile.birthDate)} });
            
            if( this.IsEmpty(this.purchaseRequest) ) {
                this.purchaseRequest = new BAPurchaseModel().load({
                    parentId: this.AthleteId,
                    productId: this.event.id,
                    priceId: this.ticket.id,
                    date: new Date(),
                    data: this.PurchaseAthleteData(this.DataLabels, this.athleteProfile),
                    requested: true,
                    completed: false,
                })
            }
        }
        this.onShowPurchaseDialog();
    }

    async ProceedToPayment() {
        try {
            const purchase = await this.savePurchaseRequest();

            if( this.ticket.IsFree ) {
                await this.completePurchaseRequest(purchase, this.athleteProfile, this.event);
                this.reloadWindow();
            } else {
                // go to Stripe to purchase the ticket
                await this.buyEventTicket(this.event, this.ticket, this.AthleteId);
            }
        } catch(e) {
            this.setError(`Unable to process purchase. Contact Best Athletes for support:\n\n${e.message}`);
            this.waitForStripe = false;
        }
    }

    IsLabelFormatRequired(label: string): boolean {
        return this.formattedFields.some(f => this.NormStringIncludes(label, this.NormalizeString(f)));
    }

    formattedFields: Array<string> = [
        'zipcode', 'postalcode', 'phone', 'email',
    ]
    IsMisformattedField(field: string): boolean {
        if( this.IsEmpty(field) ) return false;
        if( this.IsEmpty(this.formattedFields) ) return false;
        if( !this.formattedFields.some(f => this.NormStringIncludes(field, this.NormalizeString(f))) ) return false;

        const data = this.purchaseRequest.data[field];
        if( this.IsEmpty(data) ) return false;

        var validRegEx = /^[^<>"'/\\|;]+$/;
        if( this.NormStringIncludes(field, 'zipcode') ) validRegEx = /^\d{5}$|^\d{5}-\d{4}$/;
        if( this.NormStringIncludes(field, 'postalcode') ) validRegEx = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
        if( this.NormStringIncludes(field, 'phone') ) validRegEx = /^(\([2-9]\d{2}\)|[2-9]\d{2})[-. ]?[2-9]\d{2}[-. ]?\d{4}$/;
        if( this.NormStringIncludes(field, 'email') ) validRegEx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        return !validRegEx.test(data);
    }

    // update athlete 'data' from all events
    async SyncAthleteData() {
		if( this.IsEmpty(this.athleteProfile) ) return;

        const purchases: Array<BAPurchaseModel> = await baPurchaseApi.findByAthleteId(this.athleteProfile.id)
        if( this.IsNotEmpty(purchases) ) {
            const purchasesData: Record<string, any> = purchases.reduce((accumulator, p) => {
                return { ...accumulator, ...(p.data || {}) }
            }, {});
            // this.athleteProfile.data = {...purchasesData, ...this.athleteProfile.data};
            const allData: Record<string, any> = {...purchasesData, ...this.athleteProfile.data};
            const allDataArray = Object.entries(allData);
            const dataEntries = allDataArray.filter(([key,value]) => {
                return this.IsNotEmpty(value)
            })
            this.athleteProfile.data = Object.fromEntries(dataEntries);
            this.athletesCache.set(this.athleteProfile);
        }
    }
    DetermineMissingRequiredData(): boolean {
        for( const label of this.DataLabels ) {
            if( this.IsRequiredField(label, this.purchaseRequest.data) ) {
                this.requiredFieldMissing = label;
                this.isMissingRequiredData = true;
                return true;
            }
            if( this.IsMisformattedField(label) ) {
                this.requiredFieldMisformed = label;
                this.isMissingRequiredData = true;
                return true;
            }
        }
        return false;
    }
    onChangeField() {
        this.isMissingRequiredData = false;
        this.requiredFieldMissing = undefined;
        this.requiredFieldMisformed = undefined;
        this.clearStatus();
        this.DetermineMissingRequiredData();
    }

    showOnboardingDialog: boolean = false;
    onboardingComplete() {
        this.purchaseStep === PurchaseStep.Prepare
        this.showOnboardingDialog = false;
        this.onOrderRequest();
    }
    onInfo(info: string) {
        this.setInfo(info);
    }
    onError(error: string) {
        this.setError(error);
    }
}

